import React, { useState, useContext } from 'react';
import { MyContext } from '../../../../Context/MyContext';
import AppIcon from '../../../../Components/AppIcon';

const STEPS = ['property_type', 'category', 'state', 'city'];
const STEP_LABELS = {
  property_type: 'Property Type',
  category: 'Category',
  state: 'State',
  city: 'City',
};

const categoryMatchesType = (category, typeId) => {
  if (!category || !typeId) return false;
  if (Array.isArray(category.type_id)) return category.type_id.includes(typeId);
  return Number(category.type_id) === Number(typeId);
};

const PreferenceModal = ({ show, onClose }) => {
  const {
    states, cities, propertyTypes, categorys,
    savePreferences,
  } = useContext(MyContext);

  const [step, setStep] = useState('property_type');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [filteredCities, setFilteredCities] = useState([]);

  if (!show) return null;

  const stepIndex = STEPS.indexOf(step);
  const filteredCategories = (categorys || []).filter((c) => categoryMatchesType(c, selectedType?.id));

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedCategory(null);
    setStep('category');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setStep('state');
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    const cs = (cities || []).filter((c) => c.state_id === state.id);
    setFilteredCities(cs);
    setStep('city');
  };

  const handleCitySelect = async (city) => {
    try {
      await savePreferences({
        type: selectedType,
        category: selectedCategory,
        state: selectedState,
        city,
      });
    } catch { /* silent */ }
    handleClose();
  };

  const handleBack = () => {
    if (step === 'city') {
      setStep('state');
      setSelectedState(null);
      setFilteredCities([]);
    } else if (step === 'state') {
      setStep('category');
    } else if (step === 'category') {
      setStep('property_type');
      setSelectedCategory(null);
    }
  };

  const handleClose = () => {
    setStep('property_type');
    setSelectedType(null);
    setSelectedCategory(null);
    setSelectedState(null);
    setFilteredCities([]);
    onClose();
  };

  const stepTitle = () => {
    if (step === 'property_type') return 'Select Property Type';
    if (step === 'category') return 'Select Category';
    if (step === 'state') return 'Select State';
    return `Cities in ${selectedState?.state_name || ''}`;
  };

  const renderList = () => {
    if (step === 'property_type') {
      return (propertyTypes || []).map((t) => (
        <button key={t.id} type="button" onClick={() => handleTypeSelect(t)} style={itemStyle}>
          {t.type_name}
          <span>›</span>
        </button>
      ));
    }
    if (step === 'category') {
      if (!filteredCategories.length) {
        return <p style={emptyStyle}>No categories found for this property type</p>;
      }
      return filteredCategories.map((c) => (
        <button key={c.category_id ?? c.id} type="button" onClick={() => handleCategorySelect(c)} style={itemStyle}>
          {c.category_name}
          <span>›</span>
        </button>
      ));
    }
    if (step === 'state') {
      return (states || []).map((s) => (
        <button key={s.id} type="button" onClick={() => handleStateSelect(s)} style={itemStyle}>
          {s.state_name}
          <span>›</span>
        </button>
      ));
    }
    if (!filteredCities.length) {
      return <p style={emptyStyle}>No cities found</p>;
    }
    return filteredCities.map((c) => (
      <button key={c.id} type="button" onClick={() => handleCitySelect(c)} style={{ ...itemStyle, justifyContent: 'flex-start' }}>
        {c.city_name}
      </button>
    ));
  };

  return (
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          {stepIndex > 0 && (
            <button type="button" onClick={handleBack} style={backBtnStyle}>←</button>
          )}
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Let Us Know Your Preference</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>{stepTitle()}</p>
          </div>
          <button type="button" onClick={handleClose} style={closeBtnStyle} aria-label="Close"><AppIcon name="close" size={16} /></button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '4px' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: i <= stepIndex ? '#3b82f6' : '#e2e8f0',
              }} />
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: 1,
                  height: 2,
                  background: i < stepIndex ? '#3b82f6' : '#e2e8f0',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '10px', color: '#94a3b8' }}>
          {STEPS.map((s) => (
            <span key={s} style={{ color: STEPS.indexOf(s) <= stepIndex ? '#3b82f6' : '#94a3b8' }}>
              {STEP_LABELS[s]}
            </span>
          ))}
        </div>

        <div style={listStyle}>{renderList()}</div>
      </div>
    </div>
  );
};

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const modalStyle = {
  background: '#fff',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '420px',
  padding: '20px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};

const listStyle = {
  maxHeight: '300px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const itemStyle = {
  padding: '12px 14px',
  textAlign: 'left',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#f8fafc',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const emptyStyle = { textAlign: 'center', color: '#94a3b8', margin: 0 };
const backBtnStyle = { marginRight: '10px', border: 'none', background: 'transparent', fontSize: '18px', cursor: 'pointer' };
const closeBtnStyle = { border: 'none', background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' };

export default PreferenceModal;
